import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { auth as api } from '@/api/endpoints'
import { Alert, Field, Input, Spinner } from '@/components/ui'
import { useAuth } from '@/store/auth'

import { MfaSection } from './MfaSection'
import { TextSizeSection } from './TextSizeSection'

/** SettingsPresenter::actionDefault (myAccount) */
export default function MyAccountTab() {
  const { t } = useTranslation()
  const user = useAuth((s) => s.user)
  const setUser = useAuth((s) => s.setUser)

  const [profile, setProfile] = useState({
    firstname: user?.firstname ?? '',
    lastname: user?.lastname ?? '',
    phone: user?.phone ?? '',
  })
  const [passwords, setPasswords] = useState({ old_password: '', new_password: '' })
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  const saveProfile = useMutation({
    mutationFn: () => api.updateMe(profile),
    onSuccess: (updated) => {
      setUser(updated)
      setMessage({ kind: 'success', text: t('global.saved') })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  const changePassword = useMutation({
    mutationFn: () => api.changePassword(passwords.old_password, passwords.new_password),
    onSuccess: () => {
      setPasswords({ old_password: '', new_password: '' })
      setMessage({ kind: 'success', text: t('auth.changePassword') })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  return (
    <div className="max-w-xl space-y-4">
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <TextSizeSection />

      <form
        className="card space-y-3 p-4"
        onSubmit={(e) => {
          e.preventDefault()
          setMessage(null)
          saveProfile.mutate()
        }}
      >
        <h2 className="text-sm font-semibold">{t('settings.myAccount')}</h2>

        <Field label={t('auth.email')}>
          <Input value={user?.email ?? ''} readOnly />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('reservation.firstname')}>
            <Input
              value={profile.firstname}
              onChange={(e) => setProfile({ ...profile, firstname: e.target.value })}
            />
          </Field>
          <Field label={t('reservation.surname')}>
            <Input
              value={profile.lastname}
              onChange={(e) => setProfile({ ...profile, lastname: e.target.value })}
            />
          </Field>
        </div>

        <Field label={t('reservation.phone')}>
          <Input
            value={profile.phone}
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
          />
        </Field>

        <button type="submit" className="btn-primary" disabled={saveProfile.isPending}>
          {saveProfile.isPending ? <Spinner /> : null}
          {t('global.save')}
        </button>
      </form>

      <form
        className="card space-y-3 p-4"
        onSubmit={(e) => {
          e.preventDefault()
          setMessage(null)
          changePassword.mutate()
        }}
      >
        <h2 className="text-sm font-semibold">{t('auth.changePassword')}</h2>

        <Field label={t('auth.oldPassword')}>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={passwords.old_password}
            onChange={(e) => setPasswords({ ...passwords, old_password: e.target.value })}
          />
        </Field>
        <Field label={t('auth.newPassword')}>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={passwords.new_password}
            onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
          />
        </Field>

        <button type="submit" className="btn-secondary" disabled={changePassword.isPending}>
          {changePassword.isPending ? <Spinner /> : null}
          {t('auth.changePassword')}
        </button>
      </form>

      <MfaSection />
    </div>
  )
}
